import React, { useState, useEffect } from "react";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";

import AddPhotoAlternateIcon from "@material-ui/icons/AddPhotoAlternate";

import PageHeader from "designV2/components/PageHeader";
import Panel from "designV2/components/Panel";
import Field from "designV2/components/Field";
import Button from "designV2/components/Button";
import StatusBadge from "designV2/components/StatusBadge";
import { endPoint, graphqlUrl } from "utils/config";
import "../superAds.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

const UPLOAD_FILE = gql`
  mutation uploadFile($file: Upload!, $folderName: String!) {
    uploadFile(data: { file: $file, folderName: $folderName }) {
      url
    }
  }
`;

const AD_INPUT_VARS = `
  $title: String!
  $description: String
  $redirectTo: String!
  $photoUrl: String!
  $startAt: Date
  $endAt: Date
`;

const AD_INPUT_FIELDS = `
  title: $title
  description: $description
  redirectTo: $redirectTo
  photoUrl: $photoUrl
  startAt: $startAt
  endAt: $endAt
`;

const CREATE_ADVERTISEMENT = gql`
  mutation createAdvertisementByAdmin(${AD_INPUT_VARS}) {
    createAdvertisementByAdmin(input: { ${AD_INPUT_FIELDS} }) {
      _id
      title
    }
  }
`;

const UPDATE_ADVERTISEMENT = gql`
  mutation updateAdvertisementByAdmin(${AD_INPUT_VARS} $id: ID!) {
    updateAdvertisementByAdmin(id: $id, input: { ${AD_INPUT_FIELDS} }) {
      _id
      title
    }
  }
`;

const assetUrl = (path) =>
  !path ? null : /^https?:\/\//i.test(path) ? path : `${graphqlUrl}${path.startsWith("/") ? "" : "/"}${path}`;

const readableError = (error) => {
  if (!error) return "Something went wrong.";
  if (error.graphQLErrors && error.graphQLErrors.length > 0) return error.graphQLErrors[0].message;
  return error.message || "Something went wrong.";
};

const toInputDate = (value) => (value ? moment(value).format("YYYY-MM-DD") : "");

/**
 * AddAdvertisement - create or edit a banner. EditAdvertisement passes an
 * existing one in.
 *
 * `title`, `redirectTo` and `photoUrl` are all non-null on
 * AdvertisementInput, so the form blocks on them rather than letting the
 * server reject the submission — the previous version let you press Save
 * with no image and surfaced the failure as a generic modal.
 */
const AddAdvertisement = ({ advertisement }) => {
  const isEdit = Boolean(advertisement);

  const [title, setTitle] = useState(advertisement ? advertisement.title || "" : "");
  const [description, setDescription] = useState(
    advertisement ? advertisement.description || "" : ""
  );
  const [redirectTo, setRedirectTo] = useState(advertisement ? advertisement.redirectTo || "" : "");
  const [photoUrl, setPhotoUrl] = useState(advertisement ? advertisement.photoUrl || "" : "");
  const [startAt, setStartAt] = useState(advertisement ? toInputDate(advertisement.startAt) : "");
  const [endAt, setEndAt] = useState(advertisement ? toInputDate(advertisement.endAt) : "");

  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [uploadFile] = useMutation(UPLOAD_FILE);
  const [createAdvertisement] = useMutation(CREATE_ADVERTISEMENT);
  const [updateAdvertisement] = useMutation(UPDATE_ADVERTISEMENT);

  useEffect(() => {
    if (!advertisement) return;
    setTitle(advertisement.title || "");
    setDescription(advertisement.description || "");
    setRedirectTo(advertisement.redirectTo || "");
    setPhotoUrl(advertisement.photoUrl || "");
    setStartAt(toInputDate(advertisement.startAt));
    setEndAt(toInputDate(advertisement.endAt));
  }, [advertisement]);

  const onPickPhoto = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setUploading(true);
    setFormError("");
    uploadFile({ variables: { file, folderName: "advertisements" } })
      .then((res) => {
        setUploading(false);
        setPhotoUrl(res.data.uploadFile.url);
        setErrors((current) => ({ ...current, photoUrl: undefined }));
      })
      .catch((err) => {
        setUploading(false);
        setFormError(readableError(err));
      });
    event.target.value = "";
  };

  const validate = () => {
    const next = {};
    if (!title.trim()) next.title = "A title is required.";
    if (!redirectTo.trim()) {
      next.redirectTo = "Where should tapping the banner take the customer?";
    }
    if (!photoUrl) next.photoUrl = "A banner image is required.";
    if (startAt && endAt && moment(endAt).isBefore(moment(startAt))) {
      next.endAt = "The end date is before the start date.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (event) => {
    if (event) event.preventDefault();
    if (saving || uploading) return;
    setFormError("");
    if (!validate()) return;

    const variables = {
      title: title.trim(),
      description: description.trim() || undefined,
      redirectTo: redirectTo.trim(),
      photoUrl,
      startAt: startAt || undefined,
      endAt: endAt || undefined,
    };

    setSaving(true);
    const run = isEdit
      ? updateAdvertisement({ variables: { ...variables, id: advertisement._id } })
      : createAdvertisement({ variables });

    run
      .then(() => {
        window.location.href = `${endPoint}/superAdmin/advertisements`;
      })
      .catch((err) => {
        setSaving(false);
        setFormError(readableError(err));
      });
  };

  // Same derivation the list uses, so the form can say up front when a
  // banner will not actually be visible yet.
  const lifecycle = (() => {
    const now = moment();
    if (endAt && moment(endAt).isBefore(now)) return { tone: "neutral", label: "Already finished" };
    if (startAt && moment(startAt).isAfter(now)) return { tone: "info", label: "Scheduled" };
    return { tone: "success", label: "Running once saved" };
  })();

  const preview = assetUrl(photoUrl);

  return (
    <div className="page superAdsPage">
      <main className="main">
        <PageHeader
          title={isEdit ? "Edit advertisement" : "New advertisement"}
          description="A banner shown to customers in the app."
          actions={<StatusBadge status={lifecycle.tone} label={lifecycle.label} />}
        />

        <form onSubmit={onSubmit}>
          <Panel title="Banner image" subtitle="Wide images work best — this is shown full width.">
            <div className="superAdPreview">
              {preview ? (
                <React.Fragment>
                  <img className="superAdPreviewImage" src={preview} alt="Banner preview" />
                  {/* A label, not a Button: the control has to be the
                      file input's own label for clicking it to open the
                      picker. */}
                  <label className="superAdReplace">
                    <input type="file" accept="image/*" hidden onChange={onPickPhoto} />
                    {uploading ? "Uploading…" : "Replace image"}
                  </label>
                </React.Fragment>
              ) : (
                <label className="superAdDropzone">
                  <input type="file" accept="image/*" hidden onChange={onPickPhoto} />
                  <AddPhotoAlternateIcon />
                  <span>{uploading ? "Uploading…" : "Choose a banner image"}</span>
                </label>
              )}
              {errors.photoUrl && (
                <span className="dsFieldError" role="alert">
                  {errors.photoUrl}
                </span>
              )}
            </div>
          </Panel>

          <Panel title="Details">
            <div className="superAdForm">
              {formError && (
                <div className="dsFormMessage error" role="alert">
                  {formError}
                </div>
              )}

              <Field label="Title" htmlFor="title" error={errors.title} required>
                <input
                  id="title"
                  className="dsInput"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Field>

              <Field label="Description" htmlFor="description" optional>
                <textarea
                  id="description"
                  className="dsInput"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>

              <Field
                label="Links to"
                htmlFor="redirectTo"
                error={errors.redirectTo}
                required
                hint="Where tapping the banner takes the customer."
              >
                <input
                  id="redirectTo"
                  className="dsInput"
                  type="text"
                  value={redirectTo}
                  onChange={(e) => setRedirectTo(e.target.value)}
                  placeholder="https://…"
                />
              </Field>

              <div className="superAdFormRow">
                <Field
                  label="Starts"
                  htmlFor="startAt"
                  optional
                  hint="Leave blank to start straight away."
                >
                  <input
                    id="startAt"
                    className="dsInput"
                    type="date"
                    value={startAt}
                    onChange={(e) => setStartAt(e.target.value)}
                  />
                </Field>

                <Field
                  label="Ends"
                  htmlFor="endAt"
                  error={errors.endAt}
                  optional
                  hint="Leave blank to run indefinitely."
                >
                  <input
                    id="endAt"
                    className="dsInput"
                    type="date"
                    value={endAt}
                    onChange={(e) => setEndAt(e.target.value)}
                  />
                </Field>
              </div>
            </div>
          </Panel>

          <div className="superAdFooter">
            <Button
              variant="secondary"
              onClick={() => (window.location.href = `${endPoint}/superAdmin/advertisements`)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={saving} disabled={uploading}>
              {isEdit ? "Save changes" : "Create advertisement"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AddAdvertisement;
